import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type AdminPermission = 
  | 'dashboard'
  | 'court_verification'
  | 'coach_verification'
  | 'disputes'
  | 'court_complaints'
  | 'payments'
  | 'player_bans'
  | 'escrow'
  | 'payouts'
  | 'banned_courts'
  | 'user_management'
  | 'audit_logs'
  | 'reports'
  | 'fee_config'
  | 'ai_tester';

// Permission mapping: which roles can access which modules
const ROLE_PERMISSIONS: Record<string, AdminPermission[]> = {
  admin: [
    'dashboard',
    'court_verification',
    'coach_verification',
    'disputes',
    'court_complaints',
    'payments',
    'player_bans',
    'escrow',
    'payouts',
    'banned_courts',
    'user_management',
    'audit_logs',
    'reports',
    'fee_config',
    'ai_tester',
  ],
  moderator: [
    'dashboard',
    'court_verification',
    'coach_verification',
    'disputes',
    'court_complaints',
    'player_bans',
    'banned_courts',
    'reports',
    // Moderators CANNOT access: payments, escrow, payouts, user_management, audit_logs
  ],
};

export interface AdminPermissionsResult {
  isAdmin: boolean;
  isModerator: boolean;
  isSuperAdmin: boolean; // admin role = super admin
  role: 'admin' | 'moderator' | null;
  permissions: AdminPermission[];
  hasPermission: (permission: AdminPermission) => boolean;
  isLoading: boolean;
}

export function useAdminPermissions(): AdminPermissionsResult {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-permissions', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['admin', 'moderator']);

      if (error) {
        console.error('Error checking admin permissions:', error);
        return null;
      }

      const roleSet = new Set(roles?.map(r => r.role) || []);
      
      // Admin takes precedence over moderator
      if (roleSet.has('admin')) return 'admin' as const;
      if (roleSet.has('moderator')) return 'moderator' as const;
      return null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const role = data || null;
  const permissions = role ? ROLE_PERMISSIONS[role] || [] : [];

  return {
    isAdmin: role === 'admin',
    isModerator: role === 'moderator',
    isSuperAdmin: role === 'admin',
    role,
    permissions,
    hasPermission: (permission: AdminPermission) => permissions.includes(permission),
    isLoading,
  };
}
